import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import type { BaseInstance } from '@pigello/pigello-matrix';
import { useFormContext, type Path } from 'react-hook-form';
import type { FieldProps } from '../types';
import { numberregexp, parseFieldValue } from '../utils';
import { DescriptionTooltip } from './description-tooltip';

export function NumberField<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  placeholder,
  label,
  description,
  className,
  horizontal,
  disabled,
  minNumber: min,
  maxNumber: max,
  canHandleField,
  onValueChange,
  transformValueFunction,
  overrideRequired,
}: FieldProps<Instance> & { onValueChange?: (value: unknown) => void }) {
  const isRequired = overrideRequired ?? formField.required;
  const { setError, clearErrors } = useFormContext();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: { onChange: (v: string) => void; value: string }
  ): void => {
    const parsed = parseFieldValue({
      newValue: e.target.value,
      prevValue: field.value,
      regex: numberregexp,
      transformValue(value) {
        if (value === '0-')
          return transformValueFunction ? transformValueFunction('-') : '-';
        if (value === '-')
          return transformValueFunction ? transformValueFunction(value) : value;
        return transformValueFunction ? transformValueFunction(value) : value;
      },
    });

    field.onChange(parsed ?? '');
    onValueChange?.(parsed);

    // Skip validation for empty or incomplete input (allows clearing field)
    if (parsed === '' || parsed === '-') {
      clearErrors(name as Path<Instance>);
      return;
    }

    const num = Number(parsed);
    if (Number.isNaN(num)) return;

    if (max !== undefined && num > max) {
      setError(name as Path<Instance>, {
        type: 'max',
        message: `Högsta tillåtna värde är ${max}`,
      });
    } else if (min !== undefined && num < min) {
      setError(name as Path<Instance>, {
        type: 'min',
        message: `Minsta tillåtna värde är ${min}`,
      });
    } else {
      clearErrors(name as Path<Instance>);
    }
  };

  return (
    <FormField
      rules={{
        required: isRequired && 'Detta fält är obligatoriskt',
        maxLength: {
          value: 20,
          message: 'Fältet får inte vara längre än 20 tecken',
        },
        min:
          min !== undefined
            ? { value: min, message: `Minsta tillåtna värde är ${min}` }
            : undefined,
        max:
          max !== undefined
            ? { value: max, message: `Högsta tillåtna värde är ${max}` }
            : undefined,
      }}
      control={formField.control}
      name={name as Path<Instance>}
      render={({ field }) => {
        if (horizontal) {
          return (
            <FormItem
              className={cn(
                'flex items-center justify-between space-y-0',
                className
              )}
            >
              <div className='w-1/2 shrink-0'>
                <div className='flex items-center'>
                  <FormLabel>
                    {label ?? formField.label}{' '}
                    {isRequired && <span>*</span>}
                  </FormLabel>
                  {(description || formField.description) && (
                    <DescriptionTooltip
                      description={description ?? formField.description}
                    />
                  )}
                </div>

                <FormMessage />
              </div>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => handleChange(e, field)}
                  value={field.value ?? ''}
                  placeholder={placeholder ?? formField.label}
                  disabled={disabled ?? field.disabled ?? !canHandleField}
                />
              </FormControl>
            </FormItem>
          );
        } else {
          return (
            <FormItem className={className}>
              <div className='flex items-center'>
                <FormLabel>
                  {label ?? formField.label}{' '}
                  {isRequired && <span>*</span>}
                </FormLabel>
                {(description || formField.description) && (
                  <DescriptionTooltip
                    description={description ?? formField.description}
                  />
                )}
              </div>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => handleChange(e, field)}
                  value={field.value ?? ''}
                  placeholder={placeholder ?? formField.label}
                  disabled={disabled ?? field.disabled ?? !canHandleField}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }
      }}
    />
  );
}
